import { useState } from 'react'
import { supabase } from '../lib/supabase'

/** Obavezna promjena privremene lozinke (EOV-7A).
 *
 *  Radnik bez e-pošte dobija nalog sa privremenom lozinkom koju zna i admin koji mu ga je
 *  dao. Dok `profili.mora_promijeniti_lozinku` stoji, App ne pušta dalje od ovog ekrana —
 *  nema navigacije, nema preskakanja; jedini izlaz osim nove lozinke je odjava.
 *
 *  Redoslijed je bitan: prvo `auth.updateUser`, pa tek onda RPC `promijenio_lozinku`.
 *  RPC provjerava da se heš lozinke stvarno pomjerio, pa obrnuti redoslijed ne bi prošao.
 */
export function PromjenaLozinke({ ime, naPromjenu }: { ime: string; naPromjenu: () => void }) {
  const [nova, postaviNovu] = useState('')
  const [ponovo, postaviPonovo] = useState('')
  const [greska, postaviGresku] = useState<string | null>(null)
  const [uToku, postaviUToku] = useState(false)

  const prekratka = nova.length > 0 && nova.length < 8
  const neSlazuSe = ponovo.length > 0 && nova !== ponovo
  const mozeDalje = nova.length >= 8 && nova === ponovo && !uToku

  async function posalji(dogadjaj: React.FormEvent) {
    dogadjaj.preventDefault()
    postaviGresku(null)
    postaviUToku(true)

    const { error: greskaLozinke } = await supabase.auth.updateUser({ password: nova })
    if (greskaLozinke) {
      postaviUToku(false)
      // Supabase odbija lozinku koja je ista kao trenutna; to je ovdje česta greška, jer
      // radnik pokuša da ostavi privremenu.
      postaviGresku(
        greskaLozinke.message.toLowerCase().includes('different')
          ? 'Nova lozinka mora biti različita od privremene.'
          : `Lozinka nije promijenjena: ${greskaLozinke.message}`,
      )
      return
    }

    const { error: greskaZastavice } = await supabase.rpc('promijenio_lozinku')
    postaviUToku(false)

    if (greskaZastavice) {
      // Lozinka JESTE promijenjena, ali zastavica nije ugašena. Ne vraćamo radnika na
      // početak: novu lozinku već ima, treba samo da se ekran skloni.
      postaviGresku(
        `Lozinka je promijenjena, ali nalog nije otključan (${greskaZastavice.message}). ` +
          'Odjavi se i prijavi ponovo novom lozinkom; ako se ekran vrati, zovi admina.',
      )
      return
    }

    naPromjenu()
  }

  return (
    <div className="prijava">
      <form className="prijava__list" onSubmit={posalji} data-testid="promjena-forma">
        <div className="prijava__zaglavlje">
          <h1 className="marka">bookbee</h1>
          <div className="prijava__podnaslov" data-testid="promjena-podnaslov">
            Zdravo, {ime}. Lozinku koju si dobio zna i admin — postavi svoju prije nego
            nastaviš.
          </div>
        </div>

        {greska && (
          <div className="greska" role="alert" data-testid="promjena-greska">
            {greska}
          </div>
        )}

        <div className="prijava__red">
          <label className="oznaka polje-oznaka" htmlFor="polje-nova">
            Nova lozinka
          </label>
          <input
            id="polje-nova"
            className="polje"
            type="password"
            autoComplete="new-password"
            value={nova}
            onChange={(e) => postaviNovu(e.target.value)}
            data-testid="polje-nova"
          />
          {prekratka && (
            <div className="razlog" data-testid="razlog-kratka">
              Najmanje 8 znakova.
            </div>
          )}
        </div>

        <div className="prijava__red">
          <label className="oznaka polje-oznaka" htmlFor="polje-ponovo">
            Ponovi lozinku
          </label>
          <input
            id="polje-ponovo"
            className="polje"
            type="password"
            autoComplete="new-password"
            value={ponovo}
            onChange={(e) => postaviPonovo(e.target.value)}
            data-testid="polje-ponovo"
          />
          {neSlazuSe && (
            <div className="razlog" data-testid="razlog-ne-slazu">
              Lozinke se ne poklapaju.
            </div>
          )}
        </div>

        <div className="prijava__red">
          <button
            type="submit"
            className="dugme dugme--glavno"
            style={{ width: '100%' }}
            disabled={!mozeDalje}
            data-testid="dugme-promijeni"
          >
            {uToku ? 'Čuvanje…' : 'Sačuvaj i nastavi'}
          </button>
          {!mozeDalje && !uToku && (
            <div className="razlog" data-testid="promjena-razlog">
              Unesi novu lozinku dva puta, najmanje 8 znakova.
            </div>
          )}
        </div>

        <div className="prijava__red">
          <button
            type="button"
            className="veza-tekst"
            data-testid="dugme-odjava"
            onClick={() => void supabase.auth.signOut()}
          >
            Odjava
          </button>
        </div>
      </form>
    </div>
  )
}
